import { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [direction, setDirection] = useState('ltr');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    const dir = savedLanguage === 'ar' ? 'rtl' : 'ltr';
    
    setLanguage(savedLanguage);
    setDirection(dir);
    
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', savedLanguage);
  }, []);

  const changeLanguage = (lang) => {
    const dir = lang === 'ar' ? 'rtl' : 'ltr';
    
    setLanguage(lang);
    setDirection(dir);
    
    localStorage.setItem('language', lang);
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', lang);
  };

  const translations = {
    en: {
      // Navigation
      home: 'Home',
      about: 'About',
      products: 'Products',
      services: 'Services',
      contact: 'Contact',
      
      // Home Page
      welcomeTitle: 'Welcome to Kuwait Chemicals',
      welcomeSubtitle: 'Leading provider of quality medical products and pharmaceutical supplies',
      searchPlaceholder: 'Search medical products...',
      verifyPlaceholder: 'Enter verification code',
      searchBtn: 'Search',
      verifyBtn: 'Verify',
      allCategories: 'All Categories',
      
      // Product Related
      productDetails: 'Product Details',
      productDescription: 'Product Description',
      specifications: 'Specifications',
      brand: 'Brand',
      category: 'Category',
      productId: 'Product ID',
      manufacturer: 'Manufacturer',
      close: 'Close',
      qualityAssured: 'Quality assured medical product',
      
      // Company Info
      companyName: 'Kuwait Chemicals',
      companyTagline: 'Medical Excellence & Quality Assurance',
      
      // Footer
      aboutUs: 'About Us',
      quickLinks: 'Quick Links',
      contactInfo: 'Contact',
      privacyPolicy: 'Privacy Policy',
      termsConditions: 'Terms & Conditions',
      allRightsReserved: 'All rights reserved',
      
      // Messages
      loading: 'Loading...',
      noProducts: 'No products found matching your criteria.',
      noProductsFound: 'No Products Found',
      loadingMore: 'Loading more...',
      verificationSuccess: 'Product verified successfully',
      verificationError: 'Failed to verify product',
      enterVerificationCode: 'Please enter a verification code',
      
      // Sliders
      slide1Title: 'Advanced Medical Equipment',
      slide1Desc: 'State-of-the-art medical devices for healthcare professionals',
      slide2Title: 'Pharmaceutical Excellence',
      slide2Desc: 'High-quality medications and pharmaceutical products',
      slide3Title: 'Laboratory Solutions',
      slide3Desc: 'Complete laboratory equipment and diagnostic tools',
      
      // New additions for proper text formatting
      whyChooseUs: 'Why Choose Kuwait Chemicals',
      whyChooseUsDesc: 'Leading provider of quality medical products with verified excellence',
      qualityAssurance: 'Quality Assurance',
      qualityAssuranceDesc: 'Rigorous quality control for all medical products',
      certified: 'Certified Products',
      certifiedDesc: 'All products meet international medical standards',
      trustedBy: 'Trusted by Professionals',
      trustedByDesc: 'Healthcare professionals trust our products',
      innovation: 'Innovation',
      innovationDesc: 'Latest technology in medical equipment',
      patientCare: 'Patient Care',
      patientCareDesc: 'Focused on improving patient outcomes',
      verification: 'Product Verification',
      verificationDesc: 'Easy verification system for authenticity',
      ourProducts: 'Our Medical Products',
      featuredProducts: 'Featured Products',
      viewAllProducts: 'View All Products',
      exploreProducts: 'Explore Products',
      
      // Product categories with proper formatting
      medicalDevices: 'Medical Devices',
      pharmaceuticals: 'Pharmaceuticals',
      laboratorySupplies: 'Laboratory Supplies',
      diagnosticEquipment: 'Diagnostic Equipment',
      surgicalInstruments: 'Surgical Instruments',
      
      // About page content
      aboutTitle: 'About Kuwait Chemicals',
      aboutSubtitle: 'Leading the way in medical products and pharmaceutical excellence across the Middle East and beyond.',
      ourMission: 'Our Mission',
      missionText: 'At Kuwait Chemicals, we are committed to providing high-quality medical products and pharmaceutical supplies to healthcare professionals and institutions. Our advanced verification system helps ensure the authenticity and quality of our medical products, maintaining the highest standards of safety and efficacy in healthcare.',
      missionText2: 'We believe that access to reliable, high-quality medical products is fundamental to effective healthcare delivery. Through our comprehensive range of products and unwavering commitment to excellence, we support healthcare providers in delivering the best possible care to their patients.',
      whatWeDo: 'What We Do',
      whatWeDoText: 'We specialize in manufacturing and distributing medical products, pharmaceutical supplies, and healthcare equipment. Our comprehensive product catalog includes medications, medical devices, laboratory supplies, and specialized healthcare solutions.',
      whatWeDoText2: 'Each product comes with unique verification codes to ensure authenticity and quality assurance. We work closely with healthcare professionals to understand their needs and provide tailored solutions that meet the highest medical standards.',
      ourValues: 'Our Values',
      valuesSubtitle: 'The principles that guide everything we do',
      yearsExperience: 'Years of Experience',
      medicalProducts: 'Medical Products',
      healthcarePartners: 'Healthcare Partners',
      countries: 'Countries Served',
      qualityFirst: 'Quality First',
      qualityFirstDesc: 'We maintain the highest standards in all our medical products and services.',
      customerFocus: 'Customer Focus',
      customerFocusDesc: 'Our customers\' success and satisfaction drive everything we do.',
      globalReach: 'Global Reach',
      globalReachDesc: 'Serving healthcare providers across multiple countries and regions.',
      healthcareCommitment: 'Healthcare Commitment',
      healthcareCommitmentDesc: 'Dedicated to improving patient outcomes through innovative solutions.',
      excellence: 'Excellence',
      excellenceDesc: 'Recognized for outstanding quality and service in the medical industry.',
      innovationDesc: 'Continuously advancing medical technology and product development.',
      partnWithUs: 'Partner With Us',
      partnWithUsDesc: 'Join our network of healthcare partners and experience the Kuwait Chemicals difference in medical product excellence.',
      contactUs: 'Contact Us Today',
      
      // Footer enhancements
      productCategories: 'Product Categories',
      certifications: 'Certifications & Compliance',
      iso9001: 'ISO 9001:2015',
      iso13485: 'ISO 13485',
      ceMarking: 'CE Marking',
      fdaApproved: 'FDA Approved',
      gmpCertified: 'GMP Certified',
    },
    ar: {
      // Navigation
      home: 'الرئيسية',
      about: 'عنا',
      products: 'المنتجات',
      services: 'الخدمات',
      contact: 'اتصل بنا',
      
      // Home Page
      welcomeTitle: 'مرحباً بكم في الكويت للكيماويات',
      welcomeSubtitle: 'المزود الرائد للمنتجات الطبية عالية الجودة والمستلزمات الصيدلانية',
      searchPlaceholder: 'البحث في المنتجات الطبية...',
      verifyPlaceholder: 'أدخل رمز التحقق',
      searchBtn: 'بحث',
      verifyBtn: 'تحقق',
      allCategories: 'جميع الفئات',
      
      // Product Related
      productDetails: 'تفاصيل المنتج',
      productDescription: 'وصف المنتج',
      specifications: 'المواصفات',
      brand: 'العلامة التجارية',
      category: 'الفئة',
      productId: 'رقم المنتج',
      manufacturer: 'الشركة المصنعة',
      close: 'إغلاق',
      qualityAssured: 'منتج طبي مضمون الجودة',
      
      // Company Info
      companyName: 'الكويت للكيماويات',
      companyTagline: 'التميز الطبي وضمان الجودة',
      
      // Footer
      aboutUs: 'عنا',
      quickLinks: 'روابط سريعة',
      contactInfo: 'اتصل بنا',
      privacyPolicy: 'سياسة الخصوصية',
      termsConditions: 'الشروط والأحكام',
      allRightsReserved: 'جميع الحقوق محفوظة',
      
      // Messages
      loading: 'جارٍ التحميل...',
      noProducts: 'لم يتم العثور على منتجات تطابق معايير البحث.',
      noProductsFound: 'لم يتم العثور على منتجات',
      loadingMore: 'تحميل المزيد...',
      verificationSuccess: 'تم التحقق من المنتج بنجاح',
      verificationError: 'فشل في التحقق من المنتج',
      enterVerificationCode: 'يرجى إدخال رمز التحقق',
      
      // Sliders
      slide1Title: 'المعدات الطبية المتقدمة',
      slide1Desc: 'أحدث الأجهزة الطبية للمهنيين الصحيين',
      slide2Title: 'التميز الصيدلاني',
      slide2Desc: 'أدوية عالية الجودة ومنتجات صيدلانية',
      slide3Title: 'حلول المختبرات',
      slide3Desc: 'معدات مختبرية كاملة وأدوات تشخيصية',
      
      // New additions for proper text formatting
      whyChooseUs: 'لماذا تختار الكويت للكيماويات',
      whyChooseUsDesc: 'المزود الرائد للمنتجات الطبية عالية الجودة مع التميز المتحقق',
      qualityAssurance: 'ضمان الجودة',
      qualityAssuranceDesc: 'مراقبة جودة صارمة لجميع المنتجات الطبية',
      certified: 'منتجات معتمدة',
      certifiedDesc: 'جميع المنتجات تلبي المعايير الطبية الدولية',
      trustedBy: 'موثوق من المهنيين',
      trustedByDesc: 'المهنيون الصحيون يثقون بمنتجاتنا',
      innovation: 'الابتكار',
      innovationDesc: 'أحدث التقنيات في المعدات الطبية',
      patientCare: 'رعاية المرضى',
      patientCareDesc: 'نركز على تحسين نتائج المرضى',
      verification: 'التحقق من المنتج',
      verificationDesc: 'نظام تحقق سهل لضمان الأصالة',
      ourProducts: 'منتجاتنا الطبية',
      featuredProducts: 'المنتجات المميزة',
      viewAllProducts: 'عرض جميع المنتجات',
      exploreProducts: 'استكشف المنتجات',
      
      // Product categories with proper formatting
      medicalDevices: 'الأجهزة الطبية',
      pharmaceuticals: 'المنتجات الصيدلانية',
      laboratorySupplies: 'مستلزمات المختبرات',
      diagnosticEquipment: 'معدات التشخيص',
      surgicalInstruments: 'الأدوات الجراحية',
      
      // About page content
      aboutTitle: 'عن الكويت للكيماويات',
      aboutSubtitle: 'نقود الطريق في المنتجات الطبية والتميز الصيدلاني في جميع أنحاء الشرق الأوسط وما بعده.',
      ourMission: 'مهمتنا',
      missionText: 'في الكويت للكيماويات، نحن ملتزمون بتوفير منتجات طبية عالية الجودة ومستلزمات صيدلانية لمتخصصي الرعاية الصحية والمؤسسات. نظام التحقق المتقدم لدينا يساعد في ضمان صحة وجودة منتجاتنا الطبية، مع الحفاظ على أعلى معايير السلامة والفعالية في الرعاية الصحية.',
      missionText2: 'نؤمن بأن الوصول إلى منتجات طبية موثوقة وعالية الجودة هو أساس تقديم رعاية صحية فعالة. من خلال مجموعتنا الشاملة من المنتجات والتزامنا الثابت بالتميز، ندعم مقدمي الرعاية الصحية في تقديم أفضل رعاية ممكنة لمرضاهم.',
      whatWeDo: 'ما نقوم به',
      whatWeDoText: 'نتخصص في تصنيع وتوزيع المنتجات الطبية والمستلزمات الصيدلانية والمعدات الصحية. يشمل كتالوج منتجاتنا الشامل الأدوية والأجهزة الطبية ومستلزمات المختبرات والحلول الصحية المتخصصة.',
      whatWeDoText2: 'كل منتج يأتي مع رموز تحقق فريدة لضمان الأصالة وضمان الجودة. نعمل بشكل وثيق مع متخصصي الرعاية الصحية لفهم احتياجاتهم وتوفير حلول مخصصة تلبي أعلى المعايير الطبية.',
      ourValues: 'قيمنا',
      valuesSubtitle: 'المبادئ التي توجه كل ما نقوم به',
      yearsExperience: 'سنوات الخبرة',
      medicalProducts: 'منتجات طبية',
      healthcarePartners: 'شركاء الرعاية الصحية',
      countries: 'دولة نخدمها',
      qualityFirst: 'الجودة أولاً',
      qualityFirstDesc: 'نحافظ على أعلى المعايير في جميع منتجاتنا وخدماتنا الطبية.',
      customerFocus: 'التركيز على العملاء',
      customerFocusDesc: 'نجاح عملائنا ورضاهم هو ما يحرك كل ما نقوم به.',
      globalReach: 'الوصول العالمي',
      globalReachDesc: 'نخدم مقدمي الرعاية الصحية في عدة دول ومناطق.',
      healthcareCommitment: 'الالتزام بالرعاية الصحية',
      healthcareCommitmentDesc: 'مكرسون لتحسين نتائج المرضى من خلال الحلول المبتكرة.',
      excellence: 'التميز',
      excellenceDesc: 'معترف بجودتنا المتميزة وخدماتنا في الصناعة الطبية.',
      innovationDesc: 'نتقدم باستمرار في التكنولوجيا الطبية وتطوير المنتجات.',
      partnWithUs: 'اشترك معنا',
      partnWithUsDesc: 'انضم إلى شبكة شركائنا في الرعاية الصحية واختبر الفرق الذي تحدثه الكويت للكيماويات في تميز المنتجات الطبية.',
      contactUs: 'اتصل بنا اليوم',
      
      // Footer enhancements
      productCategories: 'فئات المنتجات',
      certifications: 'الشهادات والامتثال',
      iso9001: 'أيزو 9001:2015',
      iso13485: 'أيزو 13485',
      ceMarking: 'علامة CE',
      fdaApproved: 'معتمد من FDA',
      gmpCertified: 'معتمد GMP',
    }
  };

  const t = (key) => translations[language]?.[key] || key;

  return (
    <LanguageContext.Provider 
      value={{ 
        language, 
        direction, 
        changeLanguage, 
        t,
        isRTL: direction === 'rtl'
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

LanguageProvider.propTypes = {
  children: PropTypes.node.isRequired,
};